import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2 } from 'lucide-react';

/**
 * Qué se hizo para resolverlo. Se pide **al cerrar**, y sólo si la cápsula lo exige.
 *
 * **Por qué existe.** Cerrar un caso pedía un id de estado y nada más. Y sin embargo, al cerrar,
 * un LLM ya extraía síntomas, causa raíz y pasos leyendo la conversación: el sistema **le pedía a
 * un modelo que adivinara lo que la persona que resolvió sabe y no escribió**. Lo que sabe quien
 * lo resolvió se perdía, y el próximo que viera el mismo problema empezaba de cero.
 *
 * **Por qué es un diálogo y no un campo más de la ficha.** La lección está escrita en el plan y se
 * pagó cara: `activity` existía hacía dos años, viajaba en el modelo, y ninguna pantalla lo
 * mandaba — **todas** las imputaciones tenían `activity: null`. Un campo opcional se saltea
 * siempre. Éste interrumpe el cierre, que es el único momento en que la persona todavía tiene el
 * caso en la cabeza.
 *
 * **No es un trámite y el texto lo dice.** «Campo requerido» se lee como burocracia y se llena con
 * cualquier cosa. Lo que hace que alguien lo escriba de verdad es entender qué se pierde.
 */
export default function PedirLaResolucion({ abierto, estado, guardando, onCancelar, onConfirmar }) {
  const [texto, setTexto] = useState('');

  useEffect(() => { if (abierto) setTexto(''); }, [abierto]);

  return (
    <Dialog open={!!abierto} onOpenChange={(v) => { if (!v) onCancelar(); }}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="pedir-la-resolucion">
        <DialogHeader>
          <DialogTitle className="text-sm">¿Qué se hizo para resolverlo?</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <p className="text-[11px] text-zinc-500">
            Lo escribís una vez y queda para el próximo que vea el mismo problema. Sin esto, lo
            que sabés se queda en la conversación y el que sigue <b>empieza de cero</b>.
          </p>
          <textarea
            data-testid="texto-de-la-resolucion"
            autoFocus
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={'Era el índice de la tabla de clientes: estaba corrupto desde la migración.\nSe reindexó y se reinició el pool de conexiones.'}
            className="w-full border border-brand-border rounded-sm p-2 text-xs h-32 focus:ring-1 focus:ring-brand-primary focus:outline-none resize-y"
          />
          <p className="text-[10px] text-zinc-400">
            Con los pasos alcanza. No hace falta redactarlo lindo — hace falta que sea cierto.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" className="h-7 text-[11px]"
                  onClick={onCancelar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            data-testid="confirmar-resolucion"
            size="sm"
            disabled={guardando || !texto.trim()}
            onClick={() => onConfirmar(texto)}
            className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm h-7 text-[11px]"
          >
            {guardando
              ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
              : <CheckCircle2 className="w-3 h-3 mr-1" />}
            Pasar a {estado}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
